import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import { Style, Stroke, Icon } from "ol/style";
import { bd09ToMapCoord } from "./tiandituMap";

function linearEase(start, end, current, total) {
  return start + ((end - start) * current) / total;
}

function getDistance(a, b) {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
}

export default class PathAnimator {
  constructor(map, pathList, options = {}) {
    this.map = map;
    this.pathList = pathList || [];
    this.speed = options.speed || 200;
    this.iconSrc = options.iconSrc;
    this.onIndexChange = options.onIndexChange;
    this.timer = 10;
    this.i = 0;
    this.paused = false;
    this.stopped = true;
    this._intervalFlag = null;
    this._traceFeatures = [];
    this._markerFeature = null;

    this._coords = this.pathList.map((item) =>
      bd09ToMapCoord(item.longitude, item.latitude)
    );

    if (this._coords.length > 1) {
      this._baseLine = new Feature({
        geometry: new LineString(this._coords),
      });
      this._baseLine.setStyle(
        new Style({
          stroke: new Stroke({
            color: "rgba(0,0,0,0.7)",
            width: 10,
          }),
        })
      );
      map.__vectorSource.addFeature(this._baseLine);
    }

    if (this._coords.length > 0) {
      this._markerFeature = new Feature({
        geometry: new Point(this._coords[0]),
      });
      this._markerFeature.setStyle(
        new Style({
          image: new Icon({
            src: this.iconSrc,
            anchor: [0.5, 1],
            scale: 1,
          }),
        })
      );
      map.__vectorSource.addFeature(this._markerFeature);
    }
  }

  start() {
    if (!this._coords.length || this._coords.length < 2) return;
    this.stopped = false;
    this.paused = false;
    if (this.i >= this._coords.length - 1) {
      this.i = 0;
    }
    this._move(this._coords[this.i], this._coords[this.i + 1], linearEase);
  }

  pause() {
    this.paused = true;
    if (this._intervalFlag) {
      clearInterval(this._intervalFlag);
      this._intervalFlag = null;
    }
  }

  stop() {
    this.pause();
    this.stopped = true;
    this.i = 0;
    if (this._markerFeature && this._coords.length) {
      this._markerFeature.getGeometry().setCoordinates(this._coords[0]);
    }
    this._clearTraceLines();
  }

  _clearTraceLines() {
    this._traceFeatures.forEach((feature) => {
      this.map.__vectorSource.removeFeature(feature);
    });
    this._traceFeatures = [];
  }

  _move(initPos, targetPos, effect) {
    let currentCount = 0;
    const step = this.speed / (1000 / this.timer);
    const count = Math.max(1, Math.round(getDistance(initPos, targetPos) / step));

    this.map.getView().setCenter(initPos);

    const traceLine = new Feature({
      geometry: new LineString([initPos, targetPos]),
    });
    traceLine.setStyle(
      new Style({
        stroke: new Stroke({
          color: "red",
          width: 5,
        }),
      })
    );
    this.map.__vectorSource.addFeature(traceLine);
    this._traceFeatures.push(traceLine);

    if (count < 1) {
      this._onSegmentComplete();
      return;
    }

    this._intervalFlag = setInterval(() => {
      if (this.paused || this.stopped) {
        clearInterval(this._intervalFlag);
        this._intervalFlag = null;
        return;
      }

      if (currentCount >= count) {
        clearInterval(this._intervalFlag);
        this._intervalFlag = null;
        this._onSegmentComplete();
        return;
      }

      currentCount += 1;
      const x = effect(initPos[0], targetPos[0], currentCount, count);
      const y = effect(initPos[1], targetPos[1], currentCount, count);
      this._markerFeature.getGeometry().setCoordinates([x, y]);
    }, this.timer);
  }

  _onSegmentComplete() {
    if (typeof this.onIndexChange === "function") {
      this.onIndexChange(this.i + 1);
    }

    this.i += 1;
    if (this.i >= this._coords.length - 1) {
      this.pause();
      return;
    }

    if (!this.paused && !this.stopped) {
      this._move(this._coords[this.i], this._coords[this.i + 1], linearEase);
    }
  }
}
